import React from 'react';
import {
  SlideOverHeaderActionsContext,
  SpinIcon,
  stickyHeightRef,
} from '@plerionhq/design-system';
import type { ContextualViewProps } from '@plerionhq/design-system';
import { DetailHeader } from '@plerionhq/design-system/experimental';
import { AlertDetailView } from './Views/AlertDetailView';
import { AssetDetailView } from './Views/AssetDetailView';
import { FindingContextualViewSlideOverContent } from './Views/FindingsContextualView';
import { FindingsView } from './Views/FindingsView';
import { FrameworkPageView } from './Views/FrameworkPage';
import { ListAssetsViewView } from './Views/ListAssetsView';
import { AssetsWithFilteringView } from './Views/LoadAssetsView';
import { RiskDetailsView } from './Views/RiskDetails';
import { RiskFactorDetailsView } from './Views/RiskFactorDetails';
import { ScanDetailsView } from './Views/ScanDetails';
import { IssueDetailView } from './Views/IssueDetail';
import { PlatformRepositoryDetailView } from './Views/platform-repository-detail';
import { ShiftLeftFindingDetailView } from './Views/shift-left-finding-detail';
import { VulnerabilityDetailView } from './Views/VulnerabilityDetailView';

import { useTranslation } from 'react-i18next';

export const ContextualViewType = (props: ContextualViewProps) => {
  const { t } = useTranslation('containerInterface');
  switch (props.type) {
    case 'findings':
      return <FindingsView {...props} />;
    case 'assetDetail':
      return <AssetDetailView {...props} />;
    case 'alertDetail':
      return <AlertDetailView {...props} />;
    case 'findingsContextualView':
      return <FindingContextualViewSlideOverContent {...props} />;
    case 'assets':
      return <ListAssetsViewView {...props} />;
    case 'assetsWithFiltering':
      return <AssetsWithFilteringView {...props} />;
    case 'framework':
      return <FrameworkPageView {...props} />;
    case 'scanDetails':
      return <ScanDetailsView {...props} />;
    case 'riskDetail':
      return <RiskDetailsView {...props} />;
    case 'riskFactorDetail':
      return <RiskFactorDetailsView {...props} />;
    case 'vulnerabilityDetail':
      return <VulnerabilityDetailView {...props} />;
    default: {
      // Handle custom contextual view types not in the design system's ContextualViewProps union
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const untypedProps = props as any;
      if (untypedProps.type === 'issueDetail') {
        return <IssueDetailView {...untypedProps} />;
      }
      if (untypedProps.type === 'platformRepositoryDetail') {
        return <PlatformRepositoryDetailView {...untypedProps} />;
      }
      if (untypedProps.type === 'shiftLeftFindingDetail') {
        return <ShiftLeftFindingDetailView {...untypedProps} />;
      }
      return <>{t('notImplemented')}</>;
    }
  }
};

export const ContextualView = (
  props: ContextualViewProps & { close: () => void },
) => {
  const { t } = useTranslation('containerInterface');
  const { close, expandLink, hideOpenFullPage, title } = props;
  // Views render their own header actions here, before the copy link.
  const [headerActions, setHeaderActions] =
    React.useState<HTMLDivElement | null>(null);

  return (
    <div
      className="bg-lightest-gray relative flex h-full w-full flex-col overflow-auto"
      id="container-interface-contextual-view"
      data-testid="container-interface-contextual-view"
    >
      <div
        ref={stickyHeightRef('--app-sticky-top')}
        className="sticky top-0 z-10"
      >
        <DetailHeader
          variant="panel"
          title={title}
          onClose={close}
          actionsRef={setHeaderActions}
          link={expandLink}
          hideOpenFullPage={hideOpenFullPage}
        />
      </div>
      {/* Sides line up with the header; a smaller gap above the first card. */}
      <div className="px-xl pb-xl isolate flex flex-col gap-4 pt-4">
        <React.Suspense
          fallback={
            <div className="flex items-center justify-center p-4">
              {' '}
              {t('common:loading')} <SpinIcon className="h-4 w-4" />{' '}
            </div>
          }
        >
          <SlideOverHeaderActionsContext.Provider value={headerActions}>
            <ContextualViewType {...props} />
          </SlideOverHeaderActionsContext.Provider>
        </React.Suspense>
      </div>
    </div>
  );
};
